import React from 'react';

type ProgressProps = {
  progress: number;
  color: string;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const toRadians = (angle: number) => ((angle - 90) * Math.PI) / 180;

const getSectorPath = (progress: number) => {
  const clampedProgress = clamp(progress, 0, 100);
  const sectorCenter = 8;
  const sectorRadius = 4;
  const getSectorPoint = (angle: number) => ({
    x: sectorCenter + sectorRadius * Math.cos(toRadians(angle)),
    y: sectorCenter + sectorRadius * Math.sin(toRadians(angle)),
  });

  if (clampedProgress <= 0) {
    return '';
  }

  if (clampedProgress >= 100) {
    return [
      `M ${sectorCenter} ${sectorCenter}`,
      `m 0 -${sectorRadius}`,
      `a ${sectorRadius} ${sectorRadius} 0 1 1 0 ${sectorRadius * 2}`,
      `a ${sectorRadius} ${sectorRadius} 0 1 1 0 -${sectorRadius * 2}`,
    ].join(' ');
  }

  const startPoint = getSectorPoint(0);
  const endAngle = (clampedProgress / 100) * 360;
  const endPoint = getSectorPoint(endAngle);
  const largeArcFlag = endAngle > 180 ? 1 : 0;

  return [
    `M ${sectorCenter} ${sectorCenter}`,
    `L ${startPoint.x} ${startPoint.y}`,
    `A ${sectorRadius} ${sectorRadius} 0 ${largeArcFlag} 1 ${endPoint.x} ${endPoint.y}`,
    'Z',
  ].join(' ');
};

function Progress({ progress, color }: ProgressProps) {
  const clampedProgress = clamp(progress, 0, 100);
  const sectorPath = getSectorPath(clampedProgress);

  return (
    <svg
      width="var(--dimensions-200)"
      height="var(--dimensions-200)"
      viewBox="0 0 var(--dimensions-200) var(--dimensions-200)"
      role="img"
      aria-label={`Progress ${clampedProgress}%`}
    >
      <circle
        cx="var(--dimensions-100)"
        cy="var(--dimensions-100)"
        r="var(--dimensions-075)"
        fill="none"
        stroke={color}
        strokeWidth="var(--dimensions-025)"
      />
      {sectorPath && <path d={sectorPath} fill={color} />}
    </svg>
  );
}

export default Progress;
